import React, { useRef, useState } from 'react';
import { styled } from '@mui/material/styles';

import { useTranslation } from 'react-i18next';
import UIKitButton from './Button';
import UIKitIcon from './Icon';
import { ThemeColor } from '../theme';
import UIKitInput from './Input';

interface UIKitFileUploadButtonProps {
  htmlId?: string;
  text: string;
  onClick: (formData: FormData | undefined) => void;
  onError: (error: string) => void;

  className?: string; // permite agregar customizaciones
  style?: React.CSSProperties; // estilos inline para el wrapper

  disabled?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  title?: string;
  wrapperId?: string;

  /** Color del botón (usa ThemeColor: primary, secondary, etc.) */
  color?: ThemeColor;

  size?: 'large' | 'small';

  placeHolderText?: string;

  variant?: 'link' | 'flat' | 'stroke';

  allowedExtensions?: string[];
  error?: boolean;
  helperText?: string;
}

const Wrapper = styled('div')({
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '10px',
  overflow: 'hidden',
});

const HiddenInput = styled('input')({
  display: 'none',
});

const StyledPlaceholder = styled(UIKitInput)({
  width: '66.66%',

  '& .MuiFormHelperText-root': {
    marginLeft: '0px !important',
  },
});

const StyledUploadButton = styled(UIKitButton)({
  width: '33.33%',
  maxHeight: '56px',
});

const MaxSize = 30 * 1024 * 1024;

const UIKitFileUploadButton: React.FC<UIKitFileUploadButtonProps> = ({
  htmlId,
  text,
  onClick,
  onError,
  className,
  style,
  disabled = false,
  iconLeft = <UIKitIcon name="file_upload" />,
  iconRight,
  title,
  wrapperId,
  color,
  size = 'large',
  placeHolderText,
  variant = 'link',
  allowedExtensions = [],
  error,
  helperText,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();

  const themeColor: ThemeColor = color || 'primary';

  const allowed = allowedExtensions.map((ext) => ext.toLowerCase());

  const [placeholder, setPlaceholder] = useState(placeHolderText || t('exampleDoc'));

  const handleButtonClick = () => {
    if (disabled) {
      return;
    }
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const upload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      onClick(undefined);
      return;
    }

    if (file.size > MaxSize) {
      onError(t('fileOversize'));
      return;
    }

    const fileExtension = (`.${file.name.split('.').pop()}` || '').toLowerCase();

    if (allowed.length > 0 && !allowed.includes(fileExtension)) {
      onError(t('fileUnsupportedFormat'));
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    e.target.value = '';
    setPlaceholder(file ? file.name : t('exampleDoc'));
    onClick(formData);
  };

  return (
    <Wrapper className={className} id={wrapperId} style={style}>
      <StyledPlaceholder
        value={placeholder}
        readOnly
        disabled
        fullWidth
        error={error}
        helperText={helperText}
        size="small"
      />

      <StyledUploadButton
        htmlId={htmlId}
        text={text}
        variant={variant}
        color={themeColor}
        disabled={disabled}
        iconLeft={iconLeft}
        iconRight={iconRight}
        onClick={handleButtonClick}
        title={title}
        size={size}
      />

      <HiddenInput
        type="file"
        ref={inputRef}
        onChange={upload}
        disabled={disabled}
        accept={allowed.join(',')}
      />
    </Wrapper>
  );
};

export default UIKitFileUploadButton;
